import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import {
  FileText, Plus, Eye, CheckCircle, ClipboardList, GripVertical, Trash2,
  X, Save, Pencil, Search, Sparkles, ExternalLink, Archive, Clock,
  Type, AlignLeft, Mail, Phone, Hash, Calendar as CalendarIcon, ChevronDown,
  ListChecks, CheckSquare, CircleDot, ToggleRight, Star, PenLine, Link as LinkIcon,
  MapPin, Heading, Minus, Copy, Share2, Check as CheckIcon, RotateCw,
  History, Pencil as PencilEdit, Palette,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const typeIcons = {
  intake: ClipboardList, consent: CheckCircle, consultation: FileText,
  waiver: FileText, soap_note: FileText,
};
const typeColors = {
  intake: "#274F7D", consent: "#2D5936", consultation: "#805B10",
  waiver: "#8A2A23", soap_note: "#5E5E5E",
};
const CATEGORY_LABELS = {
  salon: "Salon",
  nail_tech: "Nail Tech",
  med_spa: "Med Spa",
};
const CATEGORY_COLORS = {
  salon: "#A8495A", nail_tech: "#805B10", med_spa: "#2D5936",
};
const STATUS_STYLES = {
  new: "bg-[#FFF1F2] text-rose-700 border border-rose-200",
  reviewed: "bg-[#E8F0E9] text-emerald-700 border border-emerald-200",
  archived: "bg-[#F1F5F9] text-slate-500 border border-slate-200",
};

// Field type catalog — grouped so the picker isn't an overwhelming wall.
// `hasOptions` flags fields whose editor surfaces an options input.
const FIELD_GROUPS = [
  {
    label: "Basics",
    items: [
      { value: "text", label: "Short Text", icon: Type },
      { value: "textarea", label: "Long Text", icon: AlignLeft },
      { value: "email", label: "Email", icon: Mail },
      { value: "phone", label: "Phone", icon: Phone },
      { value: "number", label: "Number", icon: Hash },
      { value: "url", label: "URL", icon: LinkIcon },
      { value: "address", label: "Address", icon: MapPin },
      { value: "date", label: "Date", icon: CalendarIcon },
      { value: "time", label: "Time", icon: Clock },
    ],
  },
  {
    label: "Choices",
    items: [
      { value: "dropdown", label: "Dropdown", icon: ChevronDown, hasOptions: true },
      { value: "radio", label: "Single Choice", icon: CircleDot, hasOptions: true },
      { value: "multi_select", label: "Multi Select", icon: ListChecks, hasOptions: true },
      { value: "checkbox", label: "Single Checkbox", icon: CheckSquare },
      { value: "yes_no", label: "Yes / No", icon: ToggleRight },
      { value: "rating", label: "Star Rating", icon: Star },
    ],
  },
  {
    label: "Layout & Sign-off",
    items: [
      { value: "heading", label: "Section Heading", icon: Heading },
      { value: "paragraph", label: "Paragraph", icon: AlignLeft },
      { value: "divider", label: "Divider", icon: Minus },
      { value: "signature", label: "Signature", icon: PenLine },
    ],
  },
];

const ALL_TYPES = FIELD_GROUPS.flatMap(g => g.items);
const typeMeta = (v) => ALL_TYPES.find(t => t.value === v) || ALL_TYPES[0];

export default function FormsPage() {
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [submissionTotal, setSubmissionTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showNew, setShowNew] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [viewingSub, setViewingSub] = useState(null);
  const [shareForm, setShareForm] = useState(null);

  const [newForm, setNewForm] = useState({ name: "", type: "intake", description: "" });

  // Builder
  const [editForm, setEditForm] = useState(null);
  const [editFields, setEditFields] = useState([]);
  const [activeFieldIdx, setActiveFieldIdx] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null); // null | number — slot where the dragged item would land
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Templates library
  const [templates, setTemplates] = useState([]);
  const [tplCategory, setTplCategory] = useState("all");
  const [creatingFromTpl, setCreatingFromTpl] = useState(null);

  // Submission filters
  const [filters, setFilters] = useState({
    form_id: "all", status: "all", search: "", start_date: "", end_date: "",
  });

  const loadForms = async () => {
    const res = await api.get("/forms");
    setForms(res.data);
  };

  const loadSubmissions = async (f = filters) => {
    const params = {};
    if (f.form_id && f.form_id !== "all") params.form_id = f.form_id;
    if (f.status && f.status !== "all") params.status = f.status;
    if (f.search) params.search = f.search;
    if (f.start_date) params.start_date = f.start_date;
    if (f.end_date) params.end_date = f.end_date;
    const res = await api.get("/form-submissions", { params });
    setSubmissions(res.data.submissions || []);
    setSubmissionTotal(res.data.total || 0);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([loadForms(), loadSubmissions()]);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // ─────────── Templates ───────────
  const openTemplates = async () => {
    setShowTemplates(true);
    if (!templates.length) {
      try {
        const res = await api.get("/forms/templates");
        setTemplates(res.data.templates || []);
      } catch { toast.error("Could not load templates"); }
    }
  };

  const createFromTemplate = async (tpl) => {
    setCreatingFromTpl(tpl.template_id);
    try {
      await api.post("/forms/from-template", { template_id: tpl.template_id });
      toast.success(`Created "${tpl.name}"`);
      setShowTemplates(false);
      await loadForms();
    } catch { toast.error("Failed to create form"); }
    setCreatingFromTpl(null);
  };

  // ─────────── Blank form ───────────
  const createForm = async () => {
    if (!newForm.name.trim()) { toast.error("Name required"); return; }
    try {
      const fields = [
        { id: `f_${Date.now()}`, label: "Full Name", type: "text", required: true },
        { id: `f_${Date.now() + 1}`, label: "Additional Notes", type: "textarea", required: false },
      ];
      await api.post("/forms", { ...newForm, fields });
      toast.success("Form created");
      setShowNew(false);
      setNewForm({ name: "", type: "intake", description: "" });
      await loadForms();
    } catch { toast.error("Failed"); }
  };

  // ─────────── Builder ───────────
  const openBuilder = (form) => {
    setEditForm(form);
    setEditFields((form.fields || []).map((f, i) => ({
      ...f, id: f.id || `f_${i}_${Date.now()}`,
    })));
    setActiveFieldIdx(null);
    setPreview(false);
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      // Persist the field schema AND the form-level metadata (description,
      // theme, submit_text) in parallel so the user only sees one "Save".
      await Promise.all([
        api.put(`/forms/${editForm.form_id}/fields`, { fields: editFields }),
        api.put(`/forms/${editForm.form_id}`, {
          description: editForm.description || "",
          theme: editForm.theme || {},
          submit_text: editForm.submit_text || "",
        }),
      ]);
      toast.success("Form saved");
      setEditForm(null);
      await loadForms();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const deleteForm = async (formId) => {
    if (!window.confirm("Delete this form? Submissions will be kept.")) return;
    try {
      await api.delete(`/forms/${formId}`);
      toast.success("Form deleted");
      await loadForms();
    } catch { toast.error("Delete failed"); }
  };

  const toggleActive = async (form) => {
    try {
      await api.put(`/forms/${form.form_id}`, { is_active: !form.is_active });
      await loadForms();
    } catch { toast.error("Could not update"); }
  };

  const addField = (type) => {
    const meta = typeMeta(type);
    const newField = {
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: meta.label,
      type,
      required: false,
      placeholder: "",
      help_text: "",
      width: "full",
    };
    if (meta.hasOptions) newField.options = ["Option 1", "Option 2", "Option 3"];
    if (type === "rating") newField.max = 5;
    setEditFields(prev => {
      const next = [...prev, newField];
      setActiveFieldIdx(next.length - 1);
      return next;
    });
  };

  const duplicateField = (idx) => {
    const original = editFields[idx];
    if (!original) return;
    const clone = {
      ...original,
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: original.label + " (copy)",
    };
    setEditFields(prev => {
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next;
    });
    setActiveFieldIdx(idx + 1);
  };

  const updateField = (idx, key, val) =>
    setEditFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));

  const removeField = (idx) => {
    setEditFields(prev => prev.filter((_, i) => i !== idx));
    setActiveFieldIdx(null);
  };

  // ── Drag and drop: track source + destination so we can render a
  //    visible drop indicator BETWEEN cards and only commit the
  //    re-order when the drop actually happens. Way less flicker than
  //    the previous implementation which moved items on every
  //    dragOver tick.
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    setDropIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleSlotDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null) return;
    setDropIdx(idx);
  };
  const handleSlotDrop = () => {
    if (dragIdx === null || dropIdx === null) return;
    setEditFields(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      // dropIdx is the slot position. If dragging downward, the slot
      // index shifts back by 1 once we remove the source.
      const target = dropIdx > dragIdx ? dropIdx - 1 : dropIdx;
      next.splice(Math.max(0, Math.min(target, next.length)), 0, moved);
      return next;
    });
    setActiveFieldIdx(dropIdx > dragIdx ? dropIdx - 1 : dropIdx);
    setDragIdx(null); setDropIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDropIdx(null); };

  // ─────────── Submissions ───────────
  const applyFilters = async () => { await loadSubmissions(filters); };
  const resetFilters = async () => {
    const fresh = { form_id: "all", status: "all", search: "", start_date: "", end_date: "" };
    setFilters(fresh);
    await loadSubmissions(fresh);
  };

  const setSubmissionStatus = async (sub, status) => {
    try {
      await api.patch(`/form-submissions/${sub.submission_id}/status`, { status });
      toast.success(`Marked as ${status}`);
      // If we're editing this submission, mirror locally too
      if (viewingSub?.submission_id === sub.submission_id) {
        setViewingSub(v => ({ ...v, status }));
      }
      await loadSubmissions();
    } catch { toast.error("Failed to update"); }
  };

  const totalSubmissions = forms.reduce((s, f) => s + (f.submission_count || 0), 0);
  const filteredTemplates = tplCategory === "all"
    ? templates : templates.filter(t => t.category === tplCategory);

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ─────────── Builder View ───────────
  if (editForm) {
    return (
      <BuilderView
        editForm={editForm}
        setEditForm={setEditForm}
        editFields={editFields}
        preview={preview} setPreview={setPreview}
        saving={saving} saveFields={saveFields}
        close={() => setEditForm(null)}
        addField={addField}
        updateField={updateField}
        removeField={removeField}
        duplicateField={duplicateField}
        activeFieldIdx={activeFieldIdx} setActiveFieldIdx={setActiveFieldIdx}
        dragIdx={dragIdx} dropIdx={dropIdx}
        handleDragStart={handleDragStart}
        handleSlotDragOver={handleSlotDragOver}
        handleSlotDrop={handleSlotDrop}
        handleDragEnd={handleDragEnd}
        onShare={() => setShareForm(editForm)}
      />
    );
  }

  // ─────────── Main View ───────────
  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="forms-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-slate-900">Forms &amp; Intake</h1>
          <p className="text-slate-600 text-sm mt-1">Digital forms, waivers, and consent documents</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="browse-templates-btn" onClick={openTemplates}
                  variant="outline"
                  className="border-rose-200 text-rose-700 hover:bg-rose-50 rounded-full h-10 px-5 text-sm">
            <Sparkles className="w-4 h-4 mr-1.5" /> Browse Templates
          </Button>
          <Button data-testid="new-form-btn" onClick={() => setShowNew(true)}
                  className="bg-slate-900 text-white rounded-full h-10 px-5 text-sm">
            <Plus className="w-4 h-4 mr-1.5" /> New Blank Form
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard icon={FileText} label="Active Forms" value={forms.filter(f => f.is_active).length} />
        <StatCard icon={CheckCircle} label="Total Submissions" value={totalSubmissions} accent="emerald" />
        <StatCard icon={ClipboardList} label="Shown in Table" value={submissionTotal} />
      </div>

      <Tabs defaultValue="forms">
        <TabsList className="bg-[#FAFAFA] p-1 rounded-lg border border-slate-200 mb-4">
          <TabsTrigger value="forms" className="text-sm">Forms</TabsTrigger>
          <TabsTrigger value="submissions" className="text-sm">Submissions</TabsTrigger>
        </TabsList>

        <TabsContent value="forms">
          {forms.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center">
              <FileText className="w-10 h-10 text-rose-300 mx-auto mb-3" />
              <h3 className="font-['Outfit'] text-lg text-slate-900 mb-1">No forms yet</h3>
              <p className="text-sm text-slate-500 mb-5">Start from a pre-built salon, nail, or med-spa template.</p>
              <Button onClick={openTemplates} data-testid="empty-browse-tpl-btn"
                      className="bg-slate-900 text-white rounded-full h-10 px-5 text-sm">
                <Sparkles className="w-4 h-4 mr-1.5" /> Browse Templates
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {forms.map(form => (
                <FormCard key={form.form_id} form={form}
                  onEdit={() => openBuilder(form)}
                  onShare={() => setShareForm(form)}
                  onDelete={() => deleteForm(form.form_id)}
                  onToggleActive={() => toggleActive(form)} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="submissions">
          <SubmissionFilters
            filters={filters} setFilters={setFilters} forms={forms}
            onApply={applyFilters} onReset={resetFilters}
          />
          <SubmissionTable
            submissions={submissions}
            onView={setViewingSub}
            onStatusChange={setSubmissionStatus}
          />
        </TabsContent>
      </Tabs>

      {/* New blank form dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-[440px] bg-white border-slate-200 rounded-2xl p-8">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium">New Blank Form</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input data-testid="form-name" value={newForm.name}
                   onChange={e => setNewForm(p => ({ ...p, name: e.target.value }))}
                   placeholder="Form name" className="h-11 border-slate-200 rounded-lg" />
            <Select value={newForm.type} onValueChange={v => setNewForm(p => ({ ...p, type: v }))}>
              <SelectTrigger className="h-11 border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="intake">Intake Form</SelectItem>
                <SelectItem value="consent">Consent Form</SelectItem>
                <SelectItem value="waiver">Waiver</SelectItem>
                <SelectItem value="consultation">Consultation</SelectItem>
                <SelectItem value="soap_note">SOAP Note</SelectItem>
              </SelectContent>
            </Select>
            <Input value={newForm.description}
                   onChange={e => setNewForm(p => ({ ...p, description: e.target.value }))}
                   placeholder="Description" className="h-11 border-slate-200 rounded-lg" />
            <Button data-testid="create-form-btn" onClick={createForm}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">
              Create Form
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template library dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="sm:max-w-[920px] max-h-[85vh] overflow-y-auto bg-white border-slate-200 rounded-2xl p-8">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-rose-500" /> Form Templates
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mt-3" data-testid="template-categories">
            {[
              { value: "all", label: "All" },
              { value: "salon", label: "Salon" },
              { value: "nail_tech", label: "Nail Tech" },
              { value: "med_spa", label: "Med Spa" },
            ].map(c => (
              <button key={c.value} onClick={() => setTplCategory(c.value)}
                      data-testid={`tpl-cat-${c.value}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${tplCategory === c.value
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
            {filteredTemplates.map(tpl => {
              const Icon = typeIcons[tpl.type] || FileText;
              return (
                <div key={tpl.template_id}
                     data-testid={`tpl-${tpl.template_id}`}
                     className="border border-slate-200 rounded-2xl p-5 hover:border-rose-300 hover:shadow-sm transition-all">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                         style={{ backgroundColor: `${CATEGORY_COLORS[tpl.category]}18` }}>
                      <Icon className="w-4 h-4" style={{ color: CATEGORY_COLORS[tpl.category] }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-slate-900 truncate">{tpl.name}</h4>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAFAFA] text-slate-500 border border-slate-200 shrink-0">
                          {CATEGORY_LABELS[tpl.category]}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{tpl.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-400 mt-3">
                    <span className="capitalize">{tpl.type.replace("_", " ")}</span>
                    <span>{tpl.fields?.length || 0} fields</span>
                  </div>
                  <Button size="sm" onClick={() => createFromTemplate(tpl)}
                          disabled={creatingFromTpl === tpl.template_id}
                          data-testid={`tpl-use-${tpl.template_id}`}
                          className="w-full mt-3 bg-slate-900 text-white hover:bg-slate-800 rounded-full h-9 text-xs">
                    {creatingFromTpl === tpl.template_id ? "Creating..." : "Use Template"}
                  </Button>
                </div>
              );
            })}
            {!templates.length && (
              <div className="md:col-span-2 text-center py-12 text-sm text-slate-400">
                Loading templates…
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share modal */}
      <ShareDialog form={shareForm} onClose={() => setShareForm(null)}
        onTokenRegenerated={(updated) => {
          // Push the freshly-issued token back into the forms list so
          // the next click shares the new URL.
          setForms(prev => prev.map(f =>
            f.form_id === updated.form_id ? { ...f, share_token: updated.share_token } : f));
          setShareForm(prev => prev ? { ...prev, share_token: updated.share_token } : null);
        }} />

      {/* Submission viewer / editor */}
      <SubmissionDialog
        sub={viewingSub} forms={forms}
        onClose={() => setViewingSub(null)}
        onSaved={async (fresh) => {
          setViewingSub(fresh);
          await loadSubmissions();
        }}
        onStatusChange={(s) => setSubmissionStatus(viewingSub, s)}
      />
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, accent }) {
  const valueClass = accent === "emerald" ? "text-emerald-600" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</span>
      </div>
      <p className={`font-['Outfit'] text-2xl font-medium ${valueClass}`}>{value}</p>
    </div>
  );
}

function FormCard({ form, onEdit, onShare, onDelete, onToggleActive }) {
  const Icon = typeIcons[form.type] || FileText;
  const color = typeColors[form.type] || "#5E5E5E";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow"
         data-testid={`form-${form.form_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
             style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <button onClick={onToggleActive} data-testid={`toggle-active-${form.form_id}`}
          className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
            form.is_active
              ? "bg-[#E8F0E9] text-emerald-600 hover:bg-emerald-100"
              : "bg-[#FAFAFA] text-slate-400 hover:bg-slate-100"
          }`}>
          {form.is_active ? "Active" : "Inactive"}
        </button>
      </div>
      <h3 className="text-base font-medium text-slate-900 mb-1">{form.name}</h3>
      <p className="text-sm text-slate-600 mb-3 line-clamp-2">{form.description}</p>
      <div className="flex items-center justify-between text-xs mb-3">
        <span className="text-slate-400 capitalize">{form.type?.replace('_', ' ')}</span>
        <span className="text-slate-900 font-medium">{form.fields?.length || 0} fields</span>
        <span className="text-rose-400">{form.submission_count || 0} submissions</span>
      </div>
      <div className="flex gap-2">
        <button onClick={onEdit}
                data-testid={`edit-form-${form.form_id}`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-slate-600 bg-[#FAFAFA] hover:bg-rose-50 transition-colors">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        <button onClick={onShare}
                data-testid={`share-form-${form.form_id}`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        <button onClick={onDelete}
                data-testid={`delete-form-${form.form_id}`}
                className="px-3 rounded-lg text-xs font-medium text-rose-600 bg-[#FAFAFA] hover:bg-rose-50 transition-colors"
                title="Delete form">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function BuilderView({
  editForm, setEditForm, editFields, preview, setPreview, saving, saveFields, close,
  addField, updateField, removeField, duplicateField,
  activeFieldIdx, setActiveFieldIdx,
  dragIdx, dropIdx, handleDragStart, handleSlotDragOver, handleSlotDrop, handleDragEnd,
  onShare,
}) {
  const activeField = activeFieldIdx !== null ? editFields[activeFieldIdx] : null;
  const [styleOpen, setStyleOpen] = useState(false);

  return (
    <div data-testid="form-builder" className="p-6 lg:p-8 max-w-[1300px] mx-auto space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 truncate">{editForm.name}</h1>
          <p className="text-sm text-slate-400">{editFields.length} fields · drag to reorder</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={onShare}
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="builder-share-btn">
            <Share2 className="w-4 h-4 mr-1" /> Share
          </Button>
          <Button size="sm" variant="outline" onClick={() => setStyleOpen(true)}
                  className="border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="builder-style-btn">
            <Palette className="w-4 h-4 mr-1" /> Style
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPreview(!preview)}
                  className="border-slate-200" data-testid="preview-toggle-btn">
            <Eye className="w-4 h-4 mr-1" /> {preview ? "Edit" : "Preview"}
          </Button>
          <Button size="sm" onClick={saveFields} disabled={saving}
                  className="bg-slate-900 text-white" data-testid="save-fields-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
          <Button size="sm" variant="ghost" onClick={close}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {preview ? (
        <BuilderPreview editForm={editForm} editFields={editFields} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[230px_1fr_300px] gap-5">
          {/* Field type catalog */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 lg:sticky lg:top-6 h-fit">
            <h3 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 px-2 font-['Manrope']">Add field</h3>
            <div className="space-y-3">
              {FIELD_GROUPS.map(group => (
                <div key={group.label}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-2 mb-1 font-['Manrope']">{group.label}</p>
                  <div className="grid grid-cols-2 gap-1">
                    {group.items.map(t => {
                      const I = t.icon;
                      return (
                        <button key={t.value} onClick={() => addField(t.value)}
                          data-testid={`add-field-${t.value}`}
                          className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg text-[11px] font-medium text-slate-700 bg-[#FAFAFA] hover:bg-rose-50 hover:text-rose-700 border border-transparent hover:border-rose-200 transition-all">
                          <I className="w-4 h-4" />
                          <span className="text-center leading-tight">{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Canvas */}
          <div onDragEnd={handleDragEnd}>
            {editFields.length === 0 && (
              <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400">
                Pick a field type from the left to start building.
              </div>
            )}
            {/* Slot before first card */}
            <DropSlot
              active={dragIdx !== null && dropIdx === 0}
              onDragOver={(e) => handleSlotDragOver(e, 0)}
              onDrop={handleSlotDrop}
            />
            {editFields.map((field, idx) => {
              const meta = typeMeta(field.type);
              const I = meta.icon;
              const isActive = activeFieldIdx === idx;
              const isDragging = dragIdx === idx;
              return (
                <div key={field.id}>
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onClick={() => setActiveFieldIdx(idx)}
                    data-testid={`builder-field-${idx}`}
                    className={`bg-white border-2 rounded-2xl p-4 mb-1 transition-all cursor-pointer ${
                      isDragging ? "opacity-30" :
                      isActive ? "border-slate-900 shadow-sm" :
                      "border-slate-200 hover:border-rose-300"
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className="cursor-grab text-slate-300 hover:text-slate-600" data-testid={`drag-handle-${idx}`}>
                        <GripVertical className="w-4 h-4" />
                      </div>
                      <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                        <I className="w-3.5 h-3.5 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{field.label}</p>
                        <p className="text-[11px] text-slate-400">{meta.label}{field.required ? " · Required" : ""}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); duplicateField(idx); }}
                        data-testid={`duplicate-field-${idx}`}
                        className="text-slate-400 hover:text-slate-700 p-1.5 rounded-md hover:bg-slate-50" title="Duplicate">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); removeField(idx); }}
                        data-testid={`remove-field-${idx}`}
                        className="text-rose-500 hover:text-rose-700 p-1.5 rounded-md hover:bg-rose-50" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Slot after this card */}
                  <DropSlot
                    active={dragIdx !== null && dropIdx === idx + 1}
                    onDragOver={(e) => handleSlotDragOver(e, idx + 1)}
                    onDrop={handleSlotDrop}
                  />
                </div>
              );
            })}
          </div>

          {/* Settings panel */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 lg:sticky lg:top-6 h-fit">
            {activeField ? (
              <FieldSettings field={activeField} idx={activeFieldIdx}
                onUpdate={(key, val) => updateField(activeFieldIdx, key, val)} />
            ) : (
              <div className="text-center py-8">
                <PencilEdit className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-400">Click a field to edit its settings</p>
              </div>
            )}
          </div>
        </div>
      )}
      <FormStyleDialog open={styleOpen} onClose={() => setStyleOpen(false)}
        form={editForm} onChange={(patch) => setEditForm(prev => ({ ...prev, ...patch }))} />
    </div>
  );
}

// Color palette curated for salon/wellness branding. Each pair is
// {accent, background} so the public form stays readable.
const FORM_THEMES = [
  { id: "cream",   label: "Cream",   bg: "#FFF8E7", accent: "#1A1A1A" },
  { id: "rose",    label: "Rose",    bg: "#FDF2F4", accent: "#A8495A" },
  { id: "sage",    label: "Sage",    bg: "#F1F4EE", accent: "#2D5936" },
  { id: "sand",    label: "Sand",    bg: "#FAF6F1", accent: "#805B10" },
  { id: "sky",     label: "Sky",     bg: "#F0F4F8", accent: "#274F7D" },
  { id: "blush",   label: "Blush",   bg: "#FBE9EC", accent: "#8A2A23" },
  { id: "lavender", label: "Lavender", bg: "#F3F0FB", accent: "#5B3FA8" },
  { id: "graphite", label: "Graphite", bg: "#F1F3F5", accent: "#1F2937" },
];

function FormStyleDialog({ open, onClose, form, onChange }) {
  if (!form) return null;
  const theme = form.theme || {};
  // Defaults match the original cream/dark palette so existing public
  // forms look identical until the owner picks something different.
  const bg = theme.background_color || "#FFF8E7";
  const accent = theme.accent_color || "#1A1A1A";

  const applyPreset = (preset) => onChange({
    theme: { ...theme, background_color: preset.bg, accent_color: preset.accent },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px] bg-white border-slate-200 rounded-2xl p-7" data-testid="form-style-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-medium flex items-center gap-2">
            <Palette className="w-5 h-5 text-violet-600" /> Form style
          </DialogTitle>
        </DialogHeader>

        <div className="mt-3 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 block font-['Manrope']">Color palette</label>
            <div className="grid grid-cols-4 gap-2">
              {FORM_THEMES.map(p => {
                const active = bg.toLowerCase() === p.bg.toLowerCase() && accent.toLowerCase() === p.accent.toLowerCase();
                return (
                  <button key={p.id} type="button" onClick={() => applyPreset(p)}
                    data-testid={`theme-preset-${p.id}`}
                    className={`relative rounded-xl border-2 overflow-hidden transition-all ${
                      active ? "border-slate-900 ring-2 ring-slate-200" : "border-slate-200 hover:border-slate-400"
                    }`}
                    style={{ backgroundColor: p.bg }}>
                    <div className="p-3 text-left">
                      <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: p.accent }}>{p.label}</div>
                      <div className="mt-2 h-2 rounded-full" style={{ backgroundColor: p.accent, width: "60%" }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Background</label>
              <div className="flex items-center gap-2">
                <input type="color" value={bg}
                  onChange={e => onChange({ theme: { ...theme, background_color: e.target.value } })}
                  data-testid="style-bg-color"
                  className="w-10 h-10 rounded cursor-pointer border border-slate-200" />
                <Input value={bg} onChange={e => onChange({ theme: { ...theme, background_color: e.target.value } })}
                  className="h-10 text-sm border-slate-200 font-mono" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Accent (button)</label>
              <div className="flex items-center gap-2">
                <input type="color" value={accent}
                  onChange={e => onChange({ theme: { ...theme, accent_color: e.target.value } })}
                  data-testid="style-accent-color"
                  className="w-10 h-10 rounded cursor-pointer border border-slate-200" />
                <Input value={accent} onChange={e => onChange({ theme: { ...theme, accent_color: e.target.value } })}
                  className="h-10 text-sm border-slate-200 font-mono" />
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Form description</label>
            <textarea value={form.description || ""} onChange={e => onChange({ description: e.target.value })}
              data-testid="style-description"
              placeholder="Shown under the title on the public form"
              className="w-full text-sm border border-slate-200 rounded-lg p-3 h-20 resize-none" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Submit button text</label>
            <Input value={form.submit_text || ""} onChange={e => onChange({ submit_text: e.target.value })}
              data-testid="style-submit-text"
              placeholder="Submit" className="h-10 text-sm border-slate-200" />
          </div>

          {/* Live mini-preview */}
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Preview</label>
            <div className="rounded-2xl p-5 border border-slate-200" style={{ backgroundColor: bg }}>
              <div className="bg-white rounded-xl p-4 border border-slate-200/50">
                <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{form.name}</p>
                {form.description && <p className="text-xs text-slate-500 mt-1">{form.description}</p>}
                <div className="mt-3 h-8 bg-slate-50 border border-slate-200 rounded-lg" />
                <button disabled className="mt-3 w-full h-10 rounded-lg text-white text-sm font-semibold"
                  style={{ backgroundColor: accent }}>
                  {form.submit_text || "Submit"}
                </button>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-slate-400">Style is saved when you click <strong>Save</strong> in the builder.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function DropSlot({ active, onDragOver, onDrop }) {
  return (
    <div onDragOver={onDragOver} onDrop={onDrop}
      className={`transition-all ${active ? "h-12" : "h-2"}`}>
      {active && (
        <div className="h-full flex items-center">
          <div className="w-full h-1 rounded-full bg-rose-400" />
        </div>
      )}
    </div>
  );
}

function FieldSettings({ field, idx, onUpdate }) {
  const meta = typeMeta(field.type);
  const showOptions = meta.hasOptions;
  const showPlaceholder = !["heading", "paragraph", "divider", "checkbox", "yes_no", "rating", "signature"].includes(field.type);
  const showRequired = !["heading", "paragraph", "divider"].includes(field.type);
  const showHelpText = !["divider"].includes(field.type);
  const showWidth = !["heading", "paragraph", "divider", "signature"].includes(field.type);
  const showDefault = ["text", "email", "phone", "url", "number", "date", "time", "address", "textarea", "dropdown"].includes(field.type);

  return (
    <div data-testid={`field-settings-${idx}`} className="space-y-4">
      <div>
        <h3 className="font-['Outfit'] font-semibold text-slate-900 text-sm">Field settings</h3>
        <p className="text-[11px] text-slate-400 mt-0.5">{meta.label}</p>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Label</label>
        <Input value={field.label || ""} onChange={e => onUpdate("label", e.target.value)}
          data-testid={`settings-label-${idx}`}
          className="h-9 text-sm border-slate-200" />
      </div>
      {showHelpText && (
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Help text</label>
          <Input value={field.help_text || ""} onChange={e => onUpdate("help_text", e.target.value)}
            data-testid={`settings-help-${idx}`}
            placeholder="Optional helper line below the label"
            className="h-9 text-sm border-slate-200" />
        </div>
      )}
      {showPlaceholder && (
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Placeholder</label>
          <Input value={field.placeholder || ""} onChange={e => onUpdate("placeholder", e.target.value)}
            data-testid={`settings-placeholder-${idx}`}
            className="h-9 text-sm border-slate-200" />
        </div>
      )}
      {showDefault && (
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Default value</label>
          <Input value={field.default_value || ""} onChange={e => onUpdate("default_value", e.target.value)}
            data-testid={`settings-default-${idx}`}
            placeholder="Pre-fill this value"
            className="h-9 text-sm border-slate-200" />
        </div>
      )}
      {showOptions && (
        <OptionsEditor
          idx={idx}
          options={field.options || []}
          onChange={(next) => onUpdate("options", next)}
        />
      )}
      {field.type === "rating" && (
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Max stars</label>
          <Select value={String(field.max || 5)} onValueChange={v => onUpdate("max", Number(v))}>
            <SelectTrigger className="h-9 text-sm border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[3, 5, 7, 10].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {showWidth && (
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block font-['Manrope']">Field width</label>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { v: "full", label: "Full" },
              { v: "half", label: "Half" },
            ].map(opt => (
              <button key={opt.v} type="button" onClick={() => onUpdate("width", opt.v)}
                data-testid={`settings-width-${opt.v}-${idx}`}
                className={`h-9 rounded-lg text-xs font-medium border transition-all ${
                  (field.width || "full") === opt.v
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {showRequired && (
        <div className="flex items-center justify-between pt-1">
          <div>
            <p className="text-sm text-slate-700 font-medium">Required</p>
            <p className="text-[11px] text-slate-400">Must be filled to submit</p>
          </div>
          <Switch checked={field.required || false} onCheckedChange={v => onUpdate("required", v)}
            data-testid={`settings-required-${idx}`} />
        </div>
      )}
    </div>
  );
}

// Dedicated options editor — one Input per option, with drag-to-reorder,
// add, remove, and bulk-paste. The previous textarea approach split on
// `\n` and filtered empties on every keystroke, which made it impossible
// to type past the existing options because pressing Enter would strip
// the blank line immediately. This component avoids that entirely.
function OptionsEditor({ idx, options, onChange }) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const update = (i, v) => {
    const next = [...options];
    next[i] = v;
    onChange(next);
  };
  const add = () => onChange([...options, `Option ${options.length + 1}`]);
  const remove = (i) => onChange(options.filter((_, j) => j !== i));
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...options];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };
  const moveDown = (i) => {
    if (i === options.length - 1) return;
    const next = [...options];
    [next[i + 1], next[i]] = [next[i], next[i + 1]];
    onChange(next);
  };

  const openBulk = () => { setBulkText(options.join("\n")); setBulkOpen(true); };
  const saveBulk = () => {
    // Split on newlines or commas, trim, drop empties. Commit happens
    // once when the user clicks Save — never on each keystroke.
    const next = bulkText
      .split(/\n|,/)
      .map(s => s.trim())
      .filter(Boolean);
    onChange(next.length ? next : ["Option 1"]);
    setBulkOpen(false);
  };

  return (
    <div data-testid={`settings-options-${idx}`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-['Manrope']">
          Options <span className="text-slate-400 font-normal lowercase tracking-normal ml-0.5">({options.length})</span>
        </label>
        <button type="button" onClick={openBulk}
          data-testid={`settings-options-bulk-${idx}`}
          className="text-[10px] text-rose-600 hover:underline">
          Bulk edit
        </button>
      </div>
      <div className="space-y-1.5">
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-1 group" data-testid={`option-row-${idx}-${i}`}>
            <div className="flex flex-col">
              <button type="button" onClick={() => moveUp(i)} disabled={i === 0}
                className="text-slate-300 hover:text-slate-700 disabled:opacity-30 text-[10px] leading-none"
                title="Move up">▲</button>
              <button type="button" onClick={() => moveDown(i)} disabled={i === options.length - 1}
                className="text-slate-300 hover:text-slate-700 disabled:opacity-30 text-[10px] leading-none"
                title="Move down">▼</button>
            </div>
            <Input value={opt}
              onChange={e => update(i, e.target.value)}
              onKeyDown={e => {
                // Press Enter on the last option to quickly add a new one
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (i === options.length - 1) add();
                }
              }}
              data-testid={`option-input-${idx}-${i}`}
              placeholder={`Option ${i + 1}`}
              className="h-8 text-sm border-slate-200 flex-1" />
            <button type="button" onClick={() => remove(i)}
              disabled={options.length <= 1}
              data-testid={`option-remove-${idx}-${i}`}
              className="p-1 text-slate-300 hover:text-rose-600 disabled:opacity-30"
              title="Remove option">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        data-testid={`settings-options-add-${idx}`}
        className="mt-2 flex items-center gap-1 text-xs text-slate-700 hover:text-rose-700 font-medium">
        <Plus className="w-3 h-3" /> Add option
      </button>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-[460px] bg-white border-slate-200 rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-lg font-medium">Bulk edit options</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">Paste a list — one option per line, or separate with commas.</p>
          <textarea value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            data-testid={`bulk-options-textarea-${idx}`}
            className="w-full h-44 text-sm border border-slate-200 rounded-lg p-3 resize-none mt-3" />
          <div className="flex justify-end gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(false)} className="h-9 text-xs">Cancel</Button>
            <Button size="sm" onClick={saveBulk}
              data-testid={`bulk-options-save-${idx}`}
              className="bg-slate-900 text-white h-9 text-xs">Save options</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function BuilderPreview({ editForm, editFields }) {
  const theme = editForm.theme || {};
  const bg = theme.background_color || "#FFF8E7";
  const accent = theme.accent_color || "#1A1A1A";
  return (
    <div data-testid="form-preview" className="rounded-3xl p-8" style={{ backgroundColor: bg }}>
      <div className="bg-white border border-slate-200 rounded-3xl p-8 max-w-2xl mx-auto">
        <h2 className="font-['Outfit'] text-2xl font-semibold text-slate-900">{editForm.name}</h2>
        {editForm.description && <p className="text-sm text-slate-500 mt-1">{editForm.description}</p>}
        <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-2 gap-x-3 gap-y-5">
          {editFields.map((f) => <PreviewField key={f.id} field={f} />)}
        </div>
        <Button disabled className="w-full rounded-full h-11 mt-6 disabled:opacity-50 text-white"
          style={{ backgroundColor: accent }}>
          {editForm.submit_text || "Submit"}
        </Button>
      </div>
    </div>
  );
}

function PreviewField({ field }) {
  const t = field.type;
  const spanFull = (field.width || "full") === "full" || ["heading", "paragraph", "divider", "signature", "textarea", "address"].includes(t);
  const colClass = spanFull ? "col-span-2" : "col-span-2 sm:col-span-1";
  if (t === "heading") return <h3 className={`font-['Outfit'] text-lg font-semibold text-slate-900 pt-2 ${colClass}`}>{field.label}</h3>;
  if (t === "paragraph") return <p className={`text-sm text-slate-600 ${colClass}`}>{field.label}</p>;
  if (t === "divider") return <hr className={`border-slate-100 ${colClass}`} />;
  return (
    <div className={colClass}>
      <label className="text-xs font-medium text-slate-600 mb-1.5 block">
        {field.label}{field.required && <span className="text-rose-600 ml-0.5">*</span>}
      </label>
      {field.help_text && <p className="text-[11px] text-slate-400 mb-1.5 -mt-1">{field.help_text}</p>}
      <PreviewInput field={field} />
    </div>
  );
}
function PreviewInput({ field }) {
  const cls = "w-full h-10 bg-[#FAFAFA] rounded-lg px-3 border border-slate-200 text-sm";
  if (field.type === "textarea") return <textarea disabled className="w-full bg-[#FAFAFA] rounded-lg px-3 py-2 border border-slate-200 text-sm h-20 resize-none" placeholder={field.placeholder} />;
  if (field.type === "dropdown") return <select disabled className={cls}><option>Select…</option>{(field.options||[]).map((o,i)=><option key={i}>{o}</option>)}</select>;
  if (field.type === "checkbox") return <label className="flex items-center gap-2"><input type="checkbox" disabled />{field.placeholder || field.label}</label>;
  if (field.type === "radio") return <div className="space-y-1">{(field.options||[]).map((o,i)=><label key={i} className="flex items-center gap-2 text-sm"><input type="radio" disabled />{o}</label>)}</div>;
  if (field.type === "multi_select") return <div className="space-y-1">{(field.options||[]).map((o,i)=><label key={i} className="flex items-center gap-2 text-sm"><input type="checkbox" disabled />{o}</label>)}</div>;
  if (field.type === "yes_no") return <div className="grid grid-cols-2 gap-2"><div className="h-10 bg-[#FAFAFA] rounded-lg border border-slate-200 text-sm flex items-center justify-center">Yes</div><div className="h-10 bg-[#FAFAFA] rounded-lg border border-slate-200 text-sm flex items-center justify-center">No</div></div>;
  if (field.type === "rating") return <div className="flex gap-1">{Array.from({length: field.max || 5}).map((_,i)=><Star key={i} className="w-6 h-6 text-amber-300" />)}</div>;
  if (field.type === "signature") return <div className="h-12 bg-[#FAFAFA] rounded-lg border border-dashed border-slate-300 flex items-center px-3 text-xs text-slate-400 italic">Sign by typing your name…</div>;
  if (field.type === "address") return <textarea disabled className="w-full bg-[#FAFAFA] rounded-lg px-3 py-2 border border-slate-200 text-sm h-14 resize-none" />;
  return <input disabled className={cls} type={field.type === "date" || field.type === "time" ? field.type : "text"} placeholder={field.placeholder} />;
}

// ─── Share dialog ────────────────────────────────────────────────
function ShareDialog({ form, onClose, onTokenRegenerated }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const open = !!form;

  const url = useMemo(() => {
    if (!form?.share_token) return "";
    return `${window.location.origin}/f/${form.share_token}`;
  }, [form]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Issuing a new link will permanently disable the current one. Continue?")) return;
    setBusy(true);
    try {
      const res = await api.post(`/forms/${form.form_id}/regenerate-link`);
      onTokenRegenerated({ form_id: form.form_id, share_token: res.data.share_token });
      toast.success("New share link issued");
    } catch { toast.error("Could not regenerate link"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px] bg-white border-slate-200 rounded-2xl p-7" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-medium flex items-center gap-2">
            <Share2 className="w-5 h-5 text-emerald-600" /> Share form
          </DialogTitle>
        </DialogHeader>
        {form && (
          <div className="mt-3 space-y-4">
            <p className="text-sm text-slate-600">
              Anyone with this link can fill out <span className="font-medium text-slate-900">{form.name}</span>.
              Their responses land in the Submissions tab.
            </p>
            <div className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-3 flex items-center gap-2">
              <input readOnly value={url}
                data-testid="share-url-input"
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-transparent text-sm text-slate-700 outline-none truncate" />
              <Button size="sm" onClick={copy} data-testid="share-copy-btn"
                className={`${copied ? "bg-emerald-600" : "bg-slate-900"} text-white rounded-lg h-8 px-3 text-xs hover:opacity-90`}>
                {copied ? <><CheckIcon className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <a href={url} target="_blank" rel="noreferrer"
                data-testid="share-open-link"
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
                <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
              </a>
              <Button variant="outline" onClick={regenerate} disabled={busy}
                data-testid="share-regenerate-btn"
                className="border-rose-200 text-rose-700 hover:bg-rose-50 h-10 text-sm">
                <RotateCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />
                Reset link
              </Button>
            </div>
            {form.is_active === false && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                Heads up — this form is currently <strong>inactive</strong>. The public link returns a &ldquo;not available&rdquo; page until you activate it.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Submissions list ────────────────────────────────────────────
function SubmissionFilters({ filters, setFilters, forms, onApply, onReset }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4" data-testid="submissions-filters">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search client name..." value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && onApply()}
            className="pl-9 h-10 border-slate-200 rounded-lg text-sm"
            data-testid="sub-search-input" />
        </div>
        <Select value={filters.form_id} onValueChange={v => setFilters(f => ({ ...f, form_id: v }))}>
          <SelectTrigger className="h-10 border-slate-200 rounded-lg text-sm" data-testid="sub-form-filter">
            <SelectValue placeholder="All forms" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All forms</SelectItem>
            {forms.map(f => (
              <SelectItem key={f.form_id} value={f.form_id}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v }))}>
          <SelectTrigger className="h-10 border-slate-200 rounded-lg text-sm" data-testid="sub-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button onClick={onApply} className="flex-1 bg-slate-900 text-white rounded-lg h-10 text-sm"
            data-testid="sub-apply-filters-btn">Filter</Button>
          <Button onClick={onReset} variant="outline" className="h-10 border-slate-200 text-sm"
            data-testid="sub-reset-filters-btn">Reset</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-wider">From</label>
          <Input type="date" value={filters.start_date}
            onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))}
            className="h-9 border-slate-200 rounded-lg text-sm mt-0.5" data-testid="sub-start-date" />
        </div>
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-wider">To</label>
          <Input type="date" value={filters.end_date}
            onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))}
            className="h-9 border-slate-200 rounded-lg text-sm mt-0.5" data-testid="sub-end-date" />
        </div>
      </div>
    </div>
  );
}

function SubmissionTable({ submissions, onView, onStatusChange }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-[#FAFAFA] border-b border-slate-200">
            <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Form</th>
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Client</th>
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
            <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Date</th>
            <th className="text-right px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map(sub => (
            <tr key={sub.submission_id} className="border-b border-slate-200 hover:bg-[#FAFAFA] cursor-pointer"
              onClick={() => onView(sub)}
              data-testid={`sub-row-${sub.submission_id}`}>
              <td className="px-6 py-3 text-sm font-medium text-slate-900">{sub.form_name}</td>
              <td className="px-4 py-3 text-sm text-slate-600">{sub.client_name || "—"}</td>
              <td className="px-4 py-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[sub.status] || STATUS_STYLES.new}`}>
                  {sub.status || "new"}
                </span>
              </td>
              <td className="px-6 py-3 text-xs text-slate-400">
                {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ""}
              </td>
              <td className="px-6 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-end gap-1">
                  <button onClick={() => onView(sub)}
                    data-testid={`sub-view-${sub.submission_id}`}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="View / edit">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                  {sub.status !== "reviewed" && (
                    <button onClick={() => onStatusChange(sub, "reviewed")}
                      data-testid={`sub-review-${sub.submission_id}`}
                      className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Mark reviewed">
                      <CheckCircle className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {sub.status !== "new" && (
                    <button onClick={() => onStatusChange(sub, "new")}
                      data-testid={`sub-unmark-${sub.submission_id}`}
                      className="p-1.5 rounded hover:bg-rose-50 text-rose-700" title="Mark as new">
                      <Clock className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {sub.status !== "archived" && (
                    <button onClick={() => onStatusChange(sub, "archived")}
                      data-testid={`sub-archive-${sub.submission_id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Archive">
                      <Archive className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {submissions.length === 0 && (
            <tr>
              <td colSpan={5} className="px-6 py-10 text-center text-sm text-slate-400">
                No submissions match these filters
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Submission viewer / editor ──────────────────────────────────
function SubmissionDialog({ sub, forms, onClose, onSaved, onStatusChange }) {
  const [editing, setEditing] = useState(false);
  const [draftResponses, setDraftResponses] = useState({});
  const [draftName, setDraftName] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const open = !!sub;

  const parentForm = forms.find(f => f.form_id === sub?.form_id);
  // Use the form's field definitions when available; otherwise infer from
  // the responses we have on file. This lets us still edit responses for
  // forms whose schema has since been changed.
  const fields = useMemo(() => {
    if (parentForm?.fields?.length) return parentForm.fields;
    return Object.keys(sub?.responses || {}).map(k => ({
      id: k, label: k, type: "text", required: false,
    }));
  }, [parentForm, sub]);

  useEffect(() => {
    if (!sub) return;
    setEditing(false);
    setShowAudit(false);
    setDraftResponses({ ...(sub.responses || {}) });
    setDraftName(sub.client_name || "");
  }, [sub]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/form-submissions/${sub.submission_id}`, {
        responses: draftResponses,
        client_name: draftName,
      });
      toast.success("Submission updated");
      setEditing(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto bg-white border-slate-200 rounded-2xl p-7" data-testid="submission-viewer">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-medium">
            {sub?.form_name}
          </DialogTitle>
        </DialogHeader>
        {sub && (
          <div className="mt-2 space-y-4">
            {/* Header row */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[sub.status] || STATUS_STYLES.new}`}>
                  {sub.status || "new"}
                </span>
                {sub.source === "public_link" && (
                  <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                    Public link
                  </span>
                )}
                <span className="text-xs text-slate-400">
                  {sub.created_at ? new Date(sub.created_at).toLocaleString() : ""}
                </span>
              </div>
              <div className="flex gap-1">
                {!editing && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setEditing(true)}
                      data-testid="sub-edit-btn"
                      className="border-slate-200 text-slate-700 h-8 text-xs">
                      <PencilEdit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    {(sub.audit_log || []).length > 0 && (
                      <Button size="sm" variant="outline" onClick={() => setShowAudit(s => !s)}
                        data-testid="sub-audit-btn"
                        className="border-slate-200 text-slate-700 h-8 text-xs">
                        <History className="w-3 h-3 mr-1" /> History ({sub.audit_log.length})
                      </Button>
                    )}
                  </>
                )}
                {editing && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraftResponses({ ...(sub.responses || {}) }); setDraftName(sub.client_name || ""); }}
                      className="h-8 text-xs">Cancel</Button>
                    <Button size="sm" onClick={save} disabled={saving}
                      data-testid="sub-save-btn"
                      className="bg-slate-900 text-white h-8 text-xs">
                      <Save className="w-3 h-3 mr-1" />{saving ? "Saving…" : "Save"}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Client info */}
            <div className="bg-slate-50 rounded-xl p-3 space-y-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-['Manrope']">Client</div>
              {editing ? (
                <Input value={draftName} onChange={e => setDraftName(e.target.value)}
                  data-testid="sub-client-name-input"
                  className="h-9 text-sm border-slate-200" />
              ) : (
                <p className="text-sm font-medium text-slate-900">{sub.client_name || "—"}</p>
              )}
              {(sub.client_email || sub.client_phone) && (
                <p className="text-xs text-slate-500">
                  {sub.client_email}{sub.client_email && sub.client_phone ? " · " : ""}{sub.client_phone}
                </p>
              )}
            </div>

            {/* Responses */}
            <div className="space-y-4">
              {fields.length === 0 && (
                <p className="text-sm text-slate-400">No responses recorded.</p>
              )}
              {fields.map(f => {
                if (["heading", "paragraph", "divider"].includes(f.type)) return null;
                const v = editing ? draftResponses[f.id] : sub.responses?.[f.id];
                return (
                  <div key={f.id}>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 font-['Manrope']">{f.label}</div>
                    {editing
                      ? <EditableResponseInput field={f}
                          value={draftResponses[f.id]}
                          onChange={(val) => setDraftResponses(prev => ({ ...prev, [f.id]: val }))} />
                      : <ResponseValue value={v} />}
                  </div>
                );
              })}
            </div>

            {/* Signature */}
            {sub.signature && (
              <div className="border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 font-['Manrope']">Signature</div>
                <p className="text-sm italic text-slate-700">{sub.signature}</p>
              </div>
            )}

            {/* Audit trail */}
            {showAudit && (sub.audit_log || []).length > 0 && (
              <div className="border-t border-slate-100 pt-4" data-testid="audit-log">
                <h4 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3 font-['Manrope']">Edit history</h4>
                <div className="space-y-3">
                  {[...sub.audit_log].reverse().map((entry, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-slate-900">{entry.by_name || "Staff"}</span>
                        <span className="text-slate-400">
                          {entry.at ? new Date(entry.at).toLocaleString() : ""}
                        </span>
                      </div>
                      <p className="text-slate-700">
                        Changed <span className="font-medium">{entry.field === "__client_name__" ? "Client name" : (fields.find(f => f.id === entry.field)?.label || entry.field)}</span>
                      </p>
                      <p className="text-slate-500 mt-1 line-through">{auditDisplay(entry.from)}</p>
                      <p className="text-slate-900 font-medium">{auditDisplay(entry.to)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status actions */}
            <div className="border-t border-slate-100 pt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={sub.status === "new"}
                onClick={() => onStatusChange("new")}
                className="text-xs">Mark new</Button>
              <Button size="sm" variant="outline" disabled={sub.status === "reviewed"}
                onClick={() => onStatusChange("reviewed")}
                className="text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50">Mark reviewed</Button>
              <Button size="sm" variant="outline" disabled={sub.status === "archived"}
                onClick={() => onStatusChange("archived")}
                className="text-xs">Archive</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function auditDisplay(v) {
  if (v === null || v === undefined || v === "") return "(empty)";
  if (Array.isArray(v)) return v.join(", ") || "(empty)";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function ResponseValue({ value }) {
  if (value === null || value === undefined || value === "") {
    return <p className="text-sm text-slate-400">—</p>;
  }
  if (Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {value.map((v, i) => (
          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{v}</span>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    return <pre className="text-xs bg-slate-50 rounded p-2 whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>;
  }
  return <p className="text-sm text-slate-900 whitespace-pre-wrap">{String(value)}</p>;
}

function EditableResponseInput({ field, value, onChange }) {
  const t = field.type || "text";
  const baseInput = "w-full h-10 bg-white rounded-lg px-3 border border-slate-200 text-sm focus:border-slate-400 outline-none";

  if (t === "textarea" || t === "address") {
    return <textarea value={value || ""} onChange={e => onChange(e.target.value)}
      rows={3} className="w-full bg-white rounded-lg px-3 py-2 border border-slate-200 text-sm focus:border-slate-400 outline-none resize-none" />;
  }
  if (t === "checkbox") {
    return <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} className="w-4 h-4" />;
  }
  if (t === "yes_no") {
    return (
      <div className="grid grid-cols-2 gap-2 max-w-xs">
        {["Yes", "No"].map(opt => (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            className={`h-9 rounded-lg border-2 text-xs font-semibold ${
              value === opt ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-600"
            }`}>{opt}</button>
        ))}
      </div>
    );
  }
  if (t === "rating") {
    const max = field.max || 5;
    const v = Number(value) || 0;
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: max }).map((_, i) => (
          <button key={i} type="button" onClick={() => onChange(i + 1)}>
            <Star className="w-5 h-5 text-amber-400" fill={i < v ? "#F59E0B" : "transparent"} />
          </button>
        ))}
        <span className="ml-2 text-xs text-slate-400">{v}/{max}</span>
      </div>
    );
  }
  if (t === "dropdown") {
    return (
      <select value={value || ""} onChange={e => onChange(e.target.value)} className={baseInput}>
        <option value="">Select…</option>
        {(field.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
  }
  if (t === "radio") {
    return (
      <div className="space-y-1">
        {(field.options || []).map((o, i) => (
          <label key={i} className="flex items-center gap-2 text-sm">
            <input type="radio" name={field.id} value={o} checked={value === o}
              onChange={() => onChange(o)} />{o}
          </label>
        ))}
      </div>
    );
  }
  if (t === "multi_select") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o]);
    return (
      <div className="space-y-1">
        {(field.options || []).map((o, i) => (
          <label key={i} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} />{o}
          </label>
        ))}
      </div>
    );
  }
  return <input type={["date", "time", "email", "phone", "number", "url"].includes(t) ? (t === "phone" ? "tel" : t) : "text"}
    value={value ?? ""} onChange={e => onChange(e.target.value)} className={baseInput} />;
}
